<template>
  <div v-if="mobile && !hideLayout" class="ios-footer-container">
    <div class="ios-footer">
      <button
        v-for="(item, index) in menuItems"
        :key="item.path"
        class="ios-footer-item"
        :class="{ active: currentIndex === index }"
        @click="navigateTo(item.path)"
      >
        <div class="icon-wrapper" :class="{ active: currentIndex === index }">
          <v-icon :size="24">{{ item.icon }}</v-icon>
        </div>
        <span class="item-label" :class="{ active: currentIndex === index }">{{ item.label }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useNavTransition } from '@/composables/useNavTransition'
const router = useRouter()
const route = useRoute()
const { mobile } = useDisplay()
const { t } = useI18n()
const { activeTab, setTab, isMainRoute } = useNavTransition()

const hideLayout = computed(() => {
  const hideRoutes = ['/register', '/login', '/special-route', '/password']
  if (hideRoutes.includes(route.path)) return true
  if (route.path.startsWith('/messages')) return true
  return false
})

const menuItems = computed(() => [
  { label: t('nav.events'), icon: 'mdi-calendar-text', path: '/' },
  { label: t('nav.teams'), icon: 'mdi-account-group', path: '/teams' },
  { label: t('nav.organizations'), icon: 'mdi-trophy', path: '/leagues' },
])

const currentIndex = computed(() => {
  // When on a main tab route, use the composable's reactive tab index
  if (isMainRoute(route.path)) {
    return activeTab.value
  }
  // Fallback for detail pages (e.g. /teams/123)
  const path = route.path
  if (path.startsWith('/teams')) return 1
  if (path.startsWith('/leagues')) return 2
  return 0
})

function navigateTo(path: string) {
  const targetIndex = menuItems.value.findIndex(item => item.path === path)
  if (targetIndex < 0) return

  if (isMainRoute(route.path)) {
    // Already inside MobileHomeView – just switch tabs (no full navigation)
    setTab(targetIndex)
    router.replace(path).catch(() => {})
  } else {
    // Coming from a detail page – navigate to the main route
    router.push(path).catch(() => {})
  }
}
</script>

<style lang="scss" scoped>
.ios-footer-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 24px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  z-index: 100;
  pointer-events: none;

  .native-app & {
    padding: 4px 8px;
    padding-bottom: max(8px, env(safe-area-inset-bottom, 0px));
  }
}

.ios-footer {
  display: flex;
  align-items: center;
  justify-content: space-around;
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  border-radius: 50px;
  padding: 4px 0;
  pointer-events: all;

  /* Dark mode (default) */
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1),
    0 4px 20px rgba(0, 0, 0, 0.15);

  /* Light mode */
  .v-theme--light & {
    background: rgba(255, 255, 255, 0.35);
    border: 1px solid rgba(0, 0, 0, 0.12);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.6),
      inset 0 -1px 0 rgba(0, 0, 0, 0.04),
      0 4px 20px rgba(0, 0, 0, 0.08);
  }
}

.ios-footer-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 50px;
  transition: all 0.2s ease;
  width: 105px;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.85);
  }

  &.active {
    background: rgba(var(--v-theme-sheetText), 0.05);
  }
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-bottom: 2px;
  color: rgba(var(--v-theme-sheetText), 0.4);
  transition: color 0.2s ease;
  position: relative;

  &.active {
    color: rgb(var(--v-theme-ice));

    /* Glow kolem ikony */
    &::before {
      content: '';
      position: absolute;
      inset: -4px;
      background: radial-gradient(circle, rgba(var(--v-theme-ice), 0.3) 0%, transparent 70%);
      border-radius: 50%;
      filter: blur(6px);
      z-index: -1;
    }

    /* Glow pod ikonou */
    &::after {
      content: '';
      position: absolute;
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
      width: 24px;
      height: 8px;
      background: radial-gradient(ellipse, rgba(var(--v-theme-ice), 0.5) 0%, transparent 70%);
      border-radius: 50%;
      filter: blur(3px);
      z-index: -1;
    }
  }
}

.item-label {
  font-size: 10px;
  font-weight: 500;
  color: rgba(var(--v-theme-sheetText), 0.35);
  transition: color 0.2s ease;

  &.active {
    color: rgb(var(--v-theme-ice));
    font-weight: 600;
  }
}

@media (min-width: 600px) {
  .ios-footer-container {
    display: none !important;
  }
}
</style>
